// Validação dos formulários do Programa de Indicações (#6196). Lógica pura.
// O banco valida de novo (fn_indicacao_registrar_abordagem); isto só dá o motivo na hora.
import { validarCPF, validarCNPJ } from '../utils.ts';
import { normalizarWhatsapp } from './telefone.ts';

// Mesmo formato de src/lib/projetos/documentos.ts. União discriminada ({ok:true}|{ok:false,…})
// não serve aqui: o tsconfig do projeto não liga o modo estrito e o `!v.ok` não estreita o tipo.
export interface Validacao {
  ok: boolean;
  erro?: string;
}

const OK: Validacao = { ok: true };
const erro = (mensagem: string): Validacao => ({ ok: false, erro: mensagem });

export const soDigitos = (v: unknown): string => String(v ?? '').replace(/\D/g, '');

export function validarDocumentoOpcional(valor: string | null | undefined): Validacao {
  const d = soDigitos(valor);
  if (!d) return OK;
  if (d.length === 11) return validarCPF(d) ? OK : erro('CPF inválido.');
  if (d.length === 14) return validarCNPJ(d) ? OK : erro('CNPJ inválido.');
  return erro('CPF/CNPJ incompleto.');
}

export interface PessoaForm {
  nome: string;
  whatsapp: string;
  cpf_cnpj: string;
}

/** Pessoa nova (quem indica): nome + WhatsApp válido ou CPF/CNPJ. */
export function validarPessoaNova(p: PessoaForm): Validacao {
  if (!p.nome.trim()) return erro('Informe o nome.');
  const doc = validarDocumentoOpcional(p.cpf_cnpj);
  if (!doc.ok) return doc;
  const temWhatsapp = p.whatsapp.trim() !== '';
  if (temWhatsapp && !normalizarWhatsapp(p.whatsapp)) return erro('WhatsApp inválido.');
  if (!temWhatsapp && !soDigitos(p.cpf_cnpj)) return erro('Informe o WhatsApp ou o CPF/CNPJ.');
  return OK;
}

export interface IndicadoForm {
  nome: string;
  whatsapp: string;
  cpf_cnpj: string;
  cidade: string;
  valor_conta: string;
}

/** "350", "350,50", "R$ 1.234,56" → número. Vazio, negativo ou inválido → null. */
export function parseValorReais(v: string): number | null {
  const limpo = v.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  if (!limpo) return null;
  const n = Number(limpo);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function validarIndicado(
  ind: IndicadoForm,
  indicador: { whatsapp?: string | null; cpf_cnpj?: string | null },
): Validacao {
  if (!ind.nome.trim()) return erro('Informe o nome do indicado.');
  const whatsapp = normalizarWhatsapp(ind.whatsapp);
  if (!whatsapp) return erro('WhatsApp do indicado inválido.');
  const doc = validarDocumentoOpcional(ind.cpf_cnpj);
  if (!doc.ok) return doc;
  const docIndicado = soDigitos(ind.cpf_cnpj);
  if (whatsapp === normalizarWhatsapp(indicador.whatsapp) || (docIndicado !== '' && docIndicado === soDigitos(indicador.cpf_cnpj))) {
    return erro('A pessoa não pode indicar a si mesma.');
  }
  if (ind.valor_conta.trim() && parseValorReais(ind.valor_conta) === null) return erro('Valor da conta inválido.');
  return OK;
}

/** Dois indicados com o mesmo WhatsApp na mesma abordagem. */
export function indicadosRepetidos(lista: Pick<IndicadoForm, 'whatsapp'>[]): boolean {
  const vistos = new Set<string>();
  for (const i of lista) {
    const w = normalizarWhatsapp(i.whatsapp);
    if (!w) continue;
    if (vistos.has(w)) return true;
    vistos.add(w);
  }
  return false;
}
